/**
 * BlockerImpact total lookup (v3.1 §3.1 / §3.2).
 *
 * Pure function — the test suite is the binding contract.
 * Default-safe: any code without an explicit classification returns
 * { blocksApproval:true, blocksSnapshot:true, displaySeverity:'ERROR' }.
 *
 * Classification is **tuple-safe**: carve-outs (advisory / APPROVAL-INFO /
 * EXPORT-family) require the full expected (domain, code pattern, rawSeverity)
 * match. HARD issues in structural domains can never be downgraded.
 */

import {
  isApprovedLeaveReadinessCode,
  isProviderResponsibilityEvidenceCode,
  KNOWN_EXPORT_BLOCKER_CODES,
  KNOWN_PAY_CONTEXT_NESTED_CODES,
  PAY_CONTEXT_RECALCULATION_CODES,
  PAYROLL_RUN_READINESS_ISSUE_CODES as C,
  PERIOD_AGGREGATOR_BLOCKER_CODES,
  providerResponsibilityEvidenceCode,
  type PayrollReadinessIssue,
  type PayrollReadinessIssueDomain,
  type PayrollReadinessRawSeverity,
} from './payroll-readiness-issue';

export {
  isApprovedLeaveReadinessCode,
  isProviderResponsibilityEvidenceCode,
  providerResponsibilityEvidenceCode,
  providerResponsibilityKindFromCode,
  PROVIDER_RESPONSIBILITY_EVIDENCE_CODE_PREFIX as PROVIDER_RESPONSIBILITY_EVIDENCE_PREFIX,
  PROVIDER_RESPONSIBILITY_EVIDENCE_CODE_SUFFIX as PROVIDER_RESPONSIBILITY_EVIDENCE_SUFFIX,
} from './payroll-readiness-issue';

/** Mirrors apps/web pay-context-shadow; kept in utils so impact is package-local. */
export type PayContextEnforcementMode = 'OFF' | 'SHADOW' | 'ENFORCE_CONTEXT' | 'ENFORCE_EXPORT';

/**
 * Roster-only policy for deciding where unresolved pay context becomes a hard
 * boundary. This is deliberately separate from PayContextEnforcementMode,
 * which remains authoritative for timesheet approval and payroll export.
 */
export type RosterPayContextAssignmentPolicy = 'LEGACY_MODE_COUPLED' | 'IDENTITY_ONLY';

export type BlockerImpact = {
  blocksApproval: boolean;
  blocksSnapshot: boolean;
  blocksExport: boolean;
  displaySeverity: 'ERROR' | 'WARNING' | 'INFO';
};

const STRUCTURAL_ERROR: BlockerImpact = {
  blocksApproval: true,
  blocksSnapshot: true,
  blocksExport: true,
  displaySeverity: 'ERROR',
};

const DEFAULT_SAFE: BlockerImpact = {
  blocksApproval: true,
  blocksSnapshot: true,
  blocksExport: true,
  displaySeverity: 'ERROR',
};

const ADVISORY_WARNING_BASE: Omit<BlockerImpact, 'blocksExport'> = {
  blocksApproval: false,
  blocksSnapshot: false,
  displaySeverity: 'WARNING',
};

const APPROVAL_INFO: BlockerImpact = {
  blocksApproval: false,
  blocksSnapshot: false,
  blocksExport: true,
  displaySeverity: 'INFO',
};

const EXPORT_WARNING: BlockerImpact = {
  blocksApproval: false,
  blocksSnapshot: false,
  blocksExport: true,
  displaySeverity: 'WARNING',
};

/**
 * Allowance evidence is saved but not yet priced.
 *
 * Pricing is payroll's to finalise and must not reach back into the timesheet
 * or rostering stage: a coordinator who records a factual allowance (handover,
 * in-charge) has done their whole job, and the row must stay approvable. This
 * previously sat in STRUCTURAL_CODES, so recording an allowance turned a
 * pending row into a red "blocked" row that no coordinator could clear —
 * reported by Medea Park on 2026-09-07 while approving weekend timesheets.
 *
 * Export no longer refused either, as of 20271975000000. The old sentence here
 * read "Export stays refused, so an unpriced allowance can never reach a pay
 * run" — true of the allowance, but it withheld the whole SHIFT. An unpriced
 * $25 line stopped a nurse being paid for the day she worked.
 *
 * The allowance still cannot reach a payslip, and that is a structural
 * property rather than a flag: a PENDING_PRICING intent is not a money line,
 * quick capture strips the component from `allowances_breakdown`, carry-forward
 * reads only that breakdown, and `pay_run_items.allowance_lines_snapshot` is
 * that same breakdown. Intents are outside all of it. Do not "strengthen" this
 * by filtering intents at the payslip — that would create the coupling the
 * exclusion depends on not having.
 */
const PRICING_PENDING_IMPACT: BlockerImpact = {
  blocksApproval: false,
  blocksSnapshot: false,
  blocksExport: false,
  displaySeverity: 'INFO',
};

/**
 * Payroll-owned pricing codes: never approval-blocking, and no longer
 * export-blocking. Payroll prices the allowance; wages do not wait on it.
 */
const PRICING_PENDING_CODES = new Set<string>([C.PROVIDER_MANUAL_ALLOWANCE_PRICING_PENDING]);

/**
 * Null/empty break-treatment status (BREAK_STATUS_UNKNOWN).
 *
 * Live gate rule: do not drop an existing approval control. Pre-P1-B the
 * legacy string filter blocked "Break treatment is NOT_RESOLVED" for this
 * row class; identity-drift is the sole intentional OLD→NEW relaxation.
 * Snapshot also validates break treatment, so this blocks all three gates.
 * Display WARNING (not ERROR) — resolvable operational control, not a hard
 * structural data defect.
 */
const BREAK_STATUS_UNKNOWN_IMPACT: BlockerImpact = {
  blocksApproval: true,
  blocksSnapshot: true,
  blocksExport: true,
  displaySeverity: 'WARNING',
};

/**
 * Domains where a HARD rawSeverity is always structural ERROR, before any
 * advisory / INFO / export carve-out. A HARD issue in these domains can never
 * be downgraded by a code-pattern match alone.
 */
const HARD_STRUCTURAL_DOMAINS = new Set<PayrollReadinessIssueDomain>([
  'PAY_CONTEXT',
  'BREAK',
  'EVIDENCE',
  'CALCULATION',
]);

/** Fact-set SOFT family — still structural (period readiness + snapshot). */
const FACT_SET_SOFT_CODES = new Set([
  'PAY_FACT_SET_REQUIRED',
  'PAY_FACT_SET_NOT_APPROVED',
  'PAY_FACTS_MISSING',
  'ALLOWANCE_PAY_CERTIFICATION_REQUIRED',
  C.PAY_EVENT_FACTS_NOT_READY,
  C.PAY_FACT_NESTED,
  // Period-loader projection of PAY_FACT_SET_NOT_APPROVED (SOFT) onto an
  // APPROVED timesheet — same family, same impact (8 Oct 2026).
  'TIMESHEET_FACT_SET_APPROVAL_PENDING',
]);

/**
 * Controlled-source readiness codes from payroll-period-readiness-loader
 * (timesheet corrections, imported leave, approved-leave treatment).
 * APPROVED_LEAVE_* family also matches `isApprovedLeaveReadinessCode`.
 */
const CONTROLLED_SOURCE_READINESS_CODES = new Set<string>([
  'TIMESHEET_CORRECTION_PENDING_REVIEW',
  'IMPORTED_LEAVE_PENDING_APPROVAL',
  'LEAVE_ROSTER_EVIDENCE_GAP',
  'LEAVE_PAY_CONTEXT_BOUNDARY',
  'LEAVE_PAY_CONTEXT_MISSING',
  'APPROVED_LEAVE_PUBLIC_HOLIDAY_PAYMENT_EVIDENCE_MISSING',
  'PUBLIC_HOLIDAY_ABSENCE_AWARD_SUPPORT_REQUIRED',
  'PUBLIC_HOLIDAY_ABSENCE_STALE_PAYMENT',
  'PUBLIC_HOLIDAY_XERO_MAPPING_REQUIRED',
  'APPROVED_LEAVE_PAY_TREATMENT_UNSUPPORTED',
]);

/** Grid + period codes that are structural approval/snapshot blockers (mode-independent). */
const STRUCTURAL_CODES = new Set<string>([
  // Pay profile with no pay-rule version cannot be priced (wave 3 C5, 15 Sep 2026)
  'PAY_PROFILE_MISSING_PAY_RULE_VERSION',
  'BREAK_DURATION_UNRESOLVED',
  // Grid structural / approval-only controls preserved per v3.1
  C.MISSING_COMPLETED_TIME_SOURCE_EVIDENCE,
  C.AUTO_CLOSED_PENDING_CONFIRMATION,
  C.IMPLAUSIBLE_WORKED_DURATION,
  C.POSSIBLE_DUPLICATE_TIMESHEET,
  C.CLOCK_HONESTY,
  C.SERVICE_AGREEMENT_GAP,
  C.CARE_PLAN_GAP,
  C.AWARD_CLASSIFICATION_MISSING,
  C.UNSUPPORTED_AWARD,
  C.AWARD_REVIEW_REQUIRED,
  C.ZERO_PAY,
  C.OPEN_PAYROLL_DISPUTE,
  C.FT_OVER_8H_WITHOUT_10H_AGREEMENT,
  C.PAY_CONTEXT_NOT_RESOLVED,
  C.BREAK_TREATMENT_BLOCKED,
  C.BREAK_TREATMENT_NOT_RESOLVED,
  C.BREAK_TREATMENT_FACTS_MISSING,
  C.BREAK_NESTED,
  C.PAY_CONTEXT_NESTED,
  // Period aggregator
  'ASSIGNMENT_PAY_CONTEXT_BLOCKED',
  'PAY_IDENTITY_CORRECTION_PENDING',
  'PAY_IDENTITY_CORRECTION_READ_UNAVAILABLE',
  'ASSIGNMENT_PAY_CONTEXT_NOT_RESOLVED',
  'ASSIGNMENT_NO_TIMESHEET',
  'TIMESHEET_ZERO_PAY',
  'TIMESHEET_PAY_CONTEXT_NOT_RESOLVED',
  'TIMESHEET_PAY_CONTEXT_STALE',
  'RATE_SCHEDULE_GAP',
  'WORKER_MISSING_PAY_PROFILE',
  'WORKED_TIME_PAYABLE_MISSING',
  'WORKED_TIME_HOLDS_READ_UNAVAILABLE',
  'EBA_EXPORT_NOT_READY',
  // Nested HARD pay-context codes (and SOFT fact-set handled separately)
  'PAY_ROLE_REQUIRED',
  'PAY_ROLE_RESELECTION_REQUIRED',
  'PAY_ROLE_DEPARTMENT_MISMATCH',
  'PAY_ROLE_CONFIRMATION_REQUIRED',
  'AWARD_RATE_VERSION_REQUIRED',
  'PAY_ROLE_ASSIGNMENT_REQUIRED',
  'PAY_ROLE_ASSIGNMENT_STALE',
  'PAY_ROLE_NOT_FOUND',
  'PAY_ROLE_NOT_ACTIVE',
  'PAY_ROLE_ELIGIBILITY_BLOCKED',
  'PAY_ROLE_AMBIGUOUS',
  'PAY_ROLE_MISSING',
  'STAFF_REQUIRED',
  'STAFF_PAY_PROFILE_NOT_FOUND',
  'STAFF_PAY_PROFILE_AMBIGUOUS',
  'STAFF_CLASSIFICATION_NOT_FOUND',
  'RATE_NOT_FOUND',
  'RATE_CONTEXT_NOT_FOUND',
  'PAY_RULE_TWO_ORACLE_CERTIFICATION_REQUIRED',
  'PAY_CONTEXT_ORDINARY_PROFILE_DRIFT',
  'SALARY_FLAT_REVIEW_REQUIRED',
  'SALARY_FLAT_CONFIG_INCOMPLETE',
  'CONTRACT_HOURLY_CONFIG_MISSING',
  'CONTRACT_HOURLY_RATE_REQUIRED',
  'CONTRACT_WEEKLY_HOURS_REQUIRED',
  'CONTRACT_OVERTIME_TERMS_REQUIRED',
  'CONTRACT_PENALTY_TERMS_REQUIRED',
  'CONTRACT_TERMS_EVIDENCE_REQUIRED',
  'CONTRACT_CONFIGURATION_NOT_APPROVED',
  'CONTRACT_HOURLY_TREATMENT_NOT_APPROVED',
  'CONTRACT_HOURLY_TREATMENT_INVALID',
  'CONTRACT_HOURLY_MATERIALISER_NOT_CONNECTED',
  // ── Queue 8f (2026-08-15): eight emitter codes that had NO explicit
  // classification and therefore fell through to DEFAULT_SAFE. Listing them
  // here is BEHAVIOUR-PRESERVING — DEFAULT_SAFE and STRUCTURAL_ERROR are the
  // same impact (blocks approval + snapshot + export, ERROR). The enumeration
  // test was red only because the classification was implicit; nothing about
  // what an operator sees or can approve changes with this commit.
  //
  // Two of them deserve a payroll conversation later, and neither is settled
  // here because either answer moves money or unblocks a gate:
  //   • TIMESHEET_REJECTED — a rejected row is terminal with no outbound
  //     transition, so blocking the whole period on it may be wrong; the
  //     alternative is excluding it from readiness entirely.
  //   • XERO_COMPONENT_LINES_MISSING — reads like an EXPORT-family code that
  //     should warn at export rather than block approval.
  'ASSIGNMENT_OFFER_NOT_RESOLVED',
  'PROVIDER_MANUAL_ALLOWANCE_RECONCILIATION_REQUIRED',
  'MANUAL_PAY_FACT_PROJECTION_PENDING',
  'TIMESHEET_REJECTED',
  'TIMESHEET_ROSTER_ASSIGNMENT_SCOPE_MISMATCH',
  'TIMESHEET_ROSTER_IDENTITY_MISMATCH',
  'TIMESHEET_ROSTER_LIFECYCLE_REVIEW_REQUIRED',
  'TIMESHEET_ROSTER_PERIOD_LINKAGE_REQUIRED',
  'XERO_COMPONENT_LINES_MISSING',
  // Approved timesheet whose priced pay facts changed after approval (HARD in
  // the emitter and the DB trigger). Was DEFAULT_SAFE — same impact, now
  // explicit (8 Oct 2026).
  'TIMESHEET_FACT_SET_CHANGED_PAY_REAPPROVAL_REQUIRED',
  // Controlled-source readiness (period loader calculation blockers)
  ...CONTROLLED_SOURCE_READINESS_CODES,
  ...FACT_SET_SOFT_CODES,
]);

const APPROVAL_INFO_CODES = new Set<string>([C.NOT_APPROVED, 'TIMESHEET_NOT_APPROVED']);

/**
 * Advisory readiness predicates (pay-run programme 15–16 Sep 2026): they name a
 * configuration that disagrees with the agreement, the worker record or the
 * provider's own pay template. They never block approval, snapshot or export —
 * the operator reads them and decides; blocking is a later, separately-decided
 * tenant policy.
 */
const ADVISORY_READINESS_CODES = new Set<string>([
  'PAY_ROLE_CARE_MINUTES_CATEGORY_MISSING',
  'APPROVED_PENDING_RUN',
  'OVERPAYMENT_REVIEW',
  'PAY_IDENTITY_CORRECTION_PENDING_APPROVAL',
  'PAY_IDENTITY_CORRECTION_PENDING_RUN',
  'PAY_IDENTITY_CORRECTION_PENDING_SETTLEMENT',
  'PAY_IDENTITY_OVERPAYMENT_DECISION_PENDING',
  'PAYMENT_ATTESTED_BY_TWO_PEOPLE',
  'LEGACY_RECEIPT_TRANSITIONAL',
  'PAY_RATE_UNSUPPORTED_BY_AGREEMENT',
  'CLASSIFICATION_DIFFERS_FROM_RECORD',
  'XERO_TEMPLATE_DISAGREES',
  'ASSIGNMENT_COVERED_BY_APPROVED_LEAVE',
  'ASSIGNMENT_SETUP_CHANGED_AFTER_APPROVAL',
  'TIMESHEET_AND_APPROVED_LEAVE_SAME_DAY',
  'WORKED_TIME_HOLDS_NEAR_PAY_DATE',
  // Owner decision 16: an undecided worked-time hold is timesheet-scoped. The held timesheet cannot be
  // approved, so it is simply not in the run (like TIMESHEET_NOT_APPROVED); the run proceeds for everyone else.
  'WORKED_TIME_HOLD_UNDECIDED',
  // MZ-004 (5 Oct 2026): a multi-timezone item whose worker base could not be read. The snapshot holds
  // only that timesheet (fail closed on the base holiday calendar); the run proceeds for everyone else.
  'PAY_CLOCK_BASE_CALENDAR_UNAVAILABLE',
  // MZ-004 F4: advisory only; the operator aligns the cycle and the roster.
  'AVERAGING_CYCLE_DIFFERS_FROM_ROSTER',
  // MZ-004 cl 29.2(a)(ii): pay is correct; only the leave credit waits on Xero setup.
  'PUBLIC_HOLIDAY_LEAVE_CREDIT_NOT_POSTABLE',
  // Leave worker scope: payroll has already confirmed the work location, so the
  // leave is priced. SOFT notice so the confirmation can be reviewed or revoked.
  'LEAVE_WORKER_SCOPE_ADJUDICATED',
]);

/**
 * An absence episode past its leave due date with no approved leave request
 * (leave programme, 14 Sep 2026). HARD (leave settled through the product)
 * blocks the snapshot; SOFT (HAND_KEYED tenants such as Medea) is advisory so
 * the run is never held for a payment the provider keys by hand.
 */
const UNPAID_PENDING_LEAVE_CODE = 'UNPAID_PENDING_LEAVE';

const BREAK_UNKNOWN_CODES = new Set<string>([C.BREAK_STATUS_UNKNOWN]);

const IDENTITY_DRIFT_SUFFIX = '_IDENTITY_DRIFT';

function isIdentityDriftCodePattern(code: string): boolean {
  return code.endsWith(IDENTITY_DRIFT_SUFFIX) || code.includes('_IDENTITY_DRIFT');
}

function isExportFamilyCodePattern(code: string): boolean {
  if (code.startsWith('XERO_MAPPING_')) return true;
  if (code.startsWith('DRY_RUN_')) return true;
  return (KNOWN_EXPORT_BLOCKER_CODES as readonly string[]).includes(code);
}

// ── Tuple-safe carve-out predicates (domain + code pattern + rawSeverity) ──

function isHardStructuralDomain(
  issue: Pick<PayrollReadinessIssue, 'domain' | 'rawSeverity'>,
): boolean {
  return issue.rawSeverity === 'HARD' && HARD_STRUCTURAL_DOMAINS.has(issue.domain);
}

/** drift → SOFT + PAY_CONTEXT + *_IDENTITY_DRIFT */
function isIdentityDriftTuple(
  issue: Pick<PayrollReadinessIssue, 'code' | 'domain' | 'rawSeverity'>,
): boolean {
  return (
    issue.domain === 'PAY_CONTEXT' &&
    issue.rawSeverity === 'SOFT' &&
    isIdentityDriftCodePattern(issue.code)
  );
}

/** NOT_APPROVED → SOFT + APPROVAL + known approval-info codes */
function isApprovalInfoTuple(
  issue: Pick<PayrollReadinessIssue, 'code' | 'domain' | 'rawSeverity'>,
): boolean {
  return (
    issue.domain === 'APPROVAL' &&
    issue.rawSeverity === 'SOFT' &&
    APPROVAL_INFO_CODES.has(issue.code)
  );
}

/** export → SOFT + EXPORT + XERO_MAPPING_* / DRY_RUN_* */
function isExportFamilyTuple(
  issue: Pick<PayrollReadinessIssue, 'code' | 'domain' | 'rawSeverity'>,
): boolean {
  return (
    issue.domain === 'EXPORT' &&
    issue.rawSeverity === 'SOFT' &&
    isExportFamilyCodePattern(issue.code)
  );
}

/**
 * BREAK NULL/unknown → SOFT + BREAK + BREAK_STATUS_UNKNOWN.
 * Blocks approval/snapshot/export (WARNING) — preserves the pre-P1-B control.
 */
function isBreakUnknownTuple(
  issue: Pick<PayrollReadinessIssue, 'code' | 'domain' | 'rawSeverity'>,
): boolean {
  return (
    issue.domain === 'BREAK' && issue.rawSeverity === 'SOFT' && BREAK_UNKNOWN_CODES.has(issue.code)
  );
}

/**
 * Returns true when the issue code has an **explicit** impact classification
 * (named set or named pattern). Used by the CI enumeration test.
 *
 * Must NOT treat inferred HARD+structural-domain as "classified" — `sampleIssueForCode`
 * defaults unknown codes to HARD + PAY_CONTEXT, which would otherwise pass the gate.
 * Runtime `blockerImpact()` still uses HARD-structural precedence + default-safe.
 */
export function isBlockerImpactExplicitlyClassified(
  issue: Pick<PayrollReadinessIssue, 'code' | 'domain' | 'rawSeverity'>,
): boolean {
  const code = issue.code;

  // Named sets
  if (PRICING_PENDING_CODES.has(code)) return true;
  if (STRUCTURAL_CODES.has(code)) return true;
  if (FACT_SET_SOFT_CODES.has(code)) return true;
  if (APPROVAL_INFO_CODES.has(code)) return true;
  if (BREAK_UNKNOWN_CODES.has(code)) return true;
  if (CONTROLLED_SOURCE_READINESS_CODES.has(code)) return true;
  if (ADVISORY_READINESS_CODES.has(code)) return true;
  if (code === UNPAID_PENDING_LEAVE_CODE) return true;

  // Explicit code patterns (not domain/severity inference)
  if (isIdentityDriftCodePattern(code)) return true;
  if (isExportFamilyCodePattern(code)) return true;
  if (isProviderResponsibilityEvidenceCode(code)) return true;
  if (isApprovedLeaveReadinessCode(code)) return true;

  return false;
}

/**
 * Total lookup: every readiness issue maps to a BlockerImpact.
 * Unclassified codes default to DEFAULT_SAFE (approval+snapshot ERROR).
 *
 * Precedence:
 * 1. HARD + structural domain → STRUCTURAL_ERROR (never downgraded)
 * 2. Full-tuple carve-outs (APPROVAL-INFO / BREAK-unknown / identity-drift / EXPORT)
 * 3. Provider-responsibility evidence prefix → STRUCTURAL_ERROR
 * 4. Enumerated structural / fact-set codes
 * 5. DEFAULT_SAFE
 *
 * Note: BREAK_STATUS_UNKNOWN is a tuple-matched classification but is
 * **approval-blocking** (WARNING). Identity-drift is the sole intentional
 * relaxation of the pre-P1-B approval string filter.
 */
export function blockerImpact(
  issue: PayrollReadinessIssue,
  mode: PayContextEnforcementMode,
): BlockerImpact {
  const code = issue.code;

  // 0) Pricing is payroll's, not the coordinator's. Ahead of HARD-structural
  // precedence so an inferred severity cannot re-block timesheet approval.
  if (PRICING_PENDING_CODES.has(code)) {
    return { ...PRICING_PENDING_IMPACT };
  }

  // 1) HARD-structural precedence — before any advisory/export/INFO carve-out
  if (isHardStructuralDomain(issue)) {
    return { ...STRUCTURAL_ERROR };
  }

  // 2) Tuple-safe classifications
  if (isApprovalInfoTuple(issue)) {
    return { ...APPROVAL_INFO };
  }

  // Null/empty break status — blocks approval (preserves legacy control)
  if (isBreakUnknownTuple(issue)) {
    return { ...BREAK_STATUS_UNKNOWN_IMPACT };
  }

  if (isIdentityDriftTuple(issue)) {
    return {
      ...ADVISORY_WARNING_BASE,
      blocksExport: mode === 'ENFORCE_EXPORT',
    };
  }

  if (isExportFamilyTuple(issue)) {
    return { ...EXPORT_WARNING };
  }

  if (ADVISORY_READINESS_CODES.has(code)) {
    return { ...ADVISORY_WARNING_BASE, blocksExport: false };
  }

  if (code === UNPAID_PENDING_LEAVE_CODE) {
    return issue.rawSeverity === 'HARD'
      ? { ...STRUCTURAL_ERROR }
      : { ...ADVISORY_WARNING_BASE, blocksExport: false };
  }

  // 3) Provider-responsibility evidence (export-readiness, isExportBlocking) —
  // structural ERROR for now (safe; matches current export-blocking behaviour).
  if (isProviderResponsibilityEvidenceCode(code)) {
    return { ...STRUCTURAL_ERROR };
  }

  // 4) Fact-set SOFT family — structural ERROR (mode-independent)
  if (FACT_SET_SOFT_CODES.has(code)) {
    return { ...STRUCTURAL_ERROR };
  }

  // Structural / controlled-source / approved-leave enumerated codes
  if (STRUCTURAL_CODES.has(code) || isApprovedLeaveReadinessCode(code)) {
    return { ...STRUCTURAL_ERROR };
  }

  // 5) Total default-safe: any code not explicitly classified above
  return { ...DEFAULT_SAFE };
}

/**
 * Codes that participate in the explicit impact table (for debugging / docs).
 * Enumeration tests must source codes from emitters, not this list alone.
 */
export function explicitlyClassifiedReadinessCodes(): string[] {
  const codes = new Set<string>();
  for (const code of PRICING_PENDING_CODES) codes.add(code);
  for (const code of STRUCTURAL_CODES) codes.add(code);
  for (const code of APPROVAL_INFO_CODES) codes.add(code);
  for (const code of ADVISORY_READINESS_CODES) codes.add(code);
  codes.add(UNPAID_PENDING_LEAVE_CODE);
  for (const code of BREAK_UNKNOWN_CODES) codes.add(code);
  for (const code of CONTROLLED_SOURCE_READINESS_CODES) codes.add(code);
  for (const code of KNOWN_PAY_CONTEXT_NESTED_CODES) codes.add(code);
  for (const code of KNOWN_EXPORT_BLOCKER_CODES) codes.add(code);
  for (const code of PERIOD_AGGREGATOR_BLOCKER_CODES) codes.add(code);
  for (const code of Object.values(C)) codes.add(code);
  // Representative open-ended family members (prefix rules cover all variants)
  codes.add(providerResponsibilityEvidenceCode('TAX'));
  codes.add('APPROVED_LEAVE_SAMPLE_REQUIRES_CONTROLLED_HANDOFF');
  return Array.from(codes).sort();
}

export function sampleIssueForCode(
  code: string,
  overrides?: Partial<PayrollReadinessIssue>,
): PayrollReadinessIssue {
  return {
    domain: inferDomainForCode(code),
    code,
    rawSeverity: inferRawSeverityForCode(code),
    resourceType: 'timesheet',
    resourceId: 'sample',
    ...overrides,
  };
}

function inferDomainForCode(code: string): PayrollReadinessIssueDomain {
  if (
    code === C.NOT_APPROVED ||
    code === 'TIMESHEET_NOT_APPROVED' ||
    code === 'TIMESHEET_CORRECTION_PENDING_REVIEW' ||
    code === 'IMPORTED_LEAVE_PENDING_APPROVAL'
  ) {
    return 'APPROVAL';
  }
  if (
    code === C.MISSING_COMPLETED_TIME_SOURCE_EVIDENCE ||
    code === C.CLOCK_HONESTY ||
    code === C.SERVICE_AGREEMENT_GAP ||
    code === C.CARE_PLAN_GAP ||
    (code.includes('EVIDENCE') &&
      !isProviderResponsibilityEvidenceCode(code) &&
      !isApprovedLeaveReadinessCode(code)) ||
    code.includes('SOURCE_EVIDENCE')
  ) {
    return 'EVIDENCE';
  }
  if (
    code === C.ZERO_PAY ||
    code === 'TIMESHEET_ZERO_PAY' ||
    code === 'ASSIGNMENT_NO_TIMESHEET' ||
    code === 'LEAVE_ROSTER_EVIDENCE_GAP' ||
    code === 'LEAVE_PAY_CONTEXT_BOUNDARY' ||
    code === 'LEAVE_PAY_CONTEXT_MISSING' ||
    isApprovedLeaveReadinessCode(code)
  ) {
    return 'CALCULATION';
  }
  if (
    code.startsWith('BREAK_') ||
    code === C.BREAK_STATUS_UNKNOWN ||
    code === C.BREAK_NESTED ||
    code === C.BREAK_TREATMENT_FACTS_MISSING
  ) {
    return 'BREAK';
  }
  if (
    isExportFamilyCodePattern(code) ||
    isProviderResponsibilityEvidenceCode(code) ||
    code === 'EBA_EXPORT_NOT_READY'
  ) {
    return 'EXPORT';
  }
  if (code === C.OPEN_PAYROLL_DISPUTE || code === C.FT_OVER_8H_WITHOUT_10H_AGREEMENT) {
    return 'APPROVAL';
  }
  return 'PAY_CONTEXT';
}

function inferRawSeverityForCode(code: string): PayrollReadinessRawSeverity {
  if (isIdentityDriftCodePattern(code)) return 'SOFT';
  if (PRICING_PENDING_CODES.has(code)) return 'SOFT';
  if (FACT_SET_SOFT_CODES.has(code)) return 'SOFT';
  if (APPROVAL_INFO_CODES.has(code)) return 'SOFT';
  if (BREAK_UNKNOWN_CODES.has(code)) return 'SOFT';
  if (isExportFamilyCodePattern(code)) return 'SOFT';
  if (ADVISORY_READINESS_CODES.has(code)) return 'SOFT';
  // Provider-responsibility evidence gaps are export-blocking HARD structural
  if (isProviderResponsibilityEvidenceCode(code)) return 'HARD';
  return 'HARD';
}

/** Exported for tests — shape of the default-safe fallback. */
export const BLOCKER_IMPACT_DEFAULT_SAFE: BlockerImpact = { ...DEFAULT_SAFE };

// ─────────────────────────────────────────────────────────────────────────────
// Separation of duties (2026-08-19): who RESOLVES a blocker.
//
// Owner directive: "push any pay context etc issues off timesheet approvals as
// that is not a coordinator problem and should be resolved by payroll".
//
// This axis is ORTHOGONAL to BlockerImpact. `blockerImpact()` is untouched and
// still answers "does this issue block approval/snapshot/export" — the money
// gates (blocksSnapshot / blocksExport) are NOT relaxed by anything below.
// `blockerResolutionOwner()` answers a different question: WHO clears the
// issue. Coordinator approval attests worked time; pay resolution belongs to
// payroll and is enforced where the money moves (the pay-run snapshot gate in
// `snapshotPayRun` refuses any timesheet whose pay context is not RESOLVED,
// and `canExport` stays false while blockers remain).
//
//   coordinator_time — worked-time integrity: the coordinator must settle it
//     before attesting the hours (evidence, duplicates, disputes, scaffolds,
//     roster-linkage integrity, compliance evidence gaps).
//   payroll_pay — pay resolution: pricing of KNOWN hours (pay context, pay
//     roles, rates, awards, allowances, pay facts, break-treatment pricing,
//     export mapping). Approval need not wait for these; the pay run still
//     refuses them.
//
// FAIL CLOSED: any code not explicitly classified below is coordinator_time —
// a new emitter code is never silently frictionless at approval.
// ─────────────────────────────────────────────────────────────────────────────

export type BlockerResolutionOwner = 'coordinator_time' | 'payroll_pay';

/**
 * Codes whose resolution belongs to PAYROLL (pay resolution of known hours).
 * Everything else — including unknown/future codes — stays coordinator_time.
 *
 * Break-treatment note: break MINUTES are recorded worked-time evidence (their
 * absence surfaces as evidence codes, which stay coordinator_time). The
 * break-treatment RESOLUTION decides whether those known minutes are paid /
 * deductible (`payrollPaidBreakMinutes` / `payrollDeductibleBreakMinutes` in
 * BreakTreatmentSnapshotV1) — it prices known recorded time, and its remedy
 * path is a payroll-role recompute or an approved pay fact, so it is
 * payroll_pay.
 */
const PAYROLL_PAY_RESOLUTION_CODES = new Set<string>([
  // MZ-004: which holiday calendar prices known hours; never worked-time integrity.
  'PAY_CLOCK_BASE_CALENDAR_UNAVAILABLE',
  'AVERAGING_CYCLE_DIFFERS_FROM_ROSTER',
  'PUBLIC_HOLIDAY_LEAVE_CREDIT_NOT_POSTABLE',
  // Pay context umbrella + nested resolver family
  'PAY_ROLE_CARE_MINUTES_CATEGORY_MISSING',
  C.PAY_CONTEXT_NOT_RESOLVED,
  C.PAY_CONTEXT_NESTED,
  // These exact emitters invalidate pricing; neither asserts missing worked
  // time. Unknown future assignment/evidence codes remain fail-closed.
  ...PAY_CONTEXT_RECALCULATION_CODES,
  'PAY_ROLE_REQUIRED',
  'PAY_ROLE_RESELECTION_REQUIRED',
  'PAY_ROLE_DEPARTMENT_MISMATCH',
  'PAY_ROLE_CONFIRMATION_REQUIRED',
  'AWARD_RATE_VERSION_REQUIRED',
  'PAY_ROLE_ASSIGNMENT_REQUIRED',
  'PAY_ROLE_ASSIGNMENT_STALE',
  'PAY_ROLE_NOT_FOUND',
  'PAY_ROLE_NOT_ACTIVE',
  'PAY_ROLE_ELIGIBILITY_BLOCKED',
  'PAY_ROLE_AMBIGUOUS',
  'PAY_ROLE_MISSING',
  'STAFF_PAY_PROFILE_NOT_FOUND',
  'STAFF_PAY_PROFILE_AMBIGUOUS',
  'STAFF_CLASSIFICATION_NOT_FOUND',
  'RATE_NOT_FOUND',
  'RATE_CONTEXT_NOT_FOUND',
  'PAY_RULE_TWO_ORACLE_CERTIFICATION_REQUIRED',
  'PAY_CONTEXT_ORDINARY_PROFILE_DRIFT',
  // pay-run programme 15–16 Sep 2026: pay setup and provider-template checks are payroll's
  'PAY_PROFILE_MISSING_PAY_RULE_VERSION',
  'PAY_RATE_UNSUPPORTED_BY_AGREEMENT',
  'CLASSIFICATION_DIFFERS_FROM_RECORD',
  'XERO_TEMPLATE_DISAGREES',
  'SALARY_FLAT_REVIEW_REQUIRED',
  'SALARY_FLAT_CONFIG_INCOMPLETE',
  'CONTRACT_HOURLY_CONFIG_MISSING',
  'CONTRACT_HOURLY_RATE_REQUIRED',
  'CONTRACT_WEEKLY_HOURS_REQUIRED',
  'CONTRACT_OVERTIME_TERMS_REQUIRED',
  'CONTRACT_PENALTY_TERMS_REQUIRED',
  'CONTRACT_TERMS_EVIDENCE_REQUIRED',
  'CONTRACT_CONFIGURATION_NOT_APPROVED',
  'CONTRACT_HOURLY_TREATMENT_NOT_APPROVED',
  'CONTRACT_HOURLY_TREATMENT_INVALID',
  'CONTRACT_HOURLY_MATERIALISER_NOT_CONNECTED',
  // Award / classification resolution (pricing of known hours)
  C.AWARD_CLASSIFICATION_MISSING,
  C.UNSUPPORTED_AWARD,
  C.AWARD_REVIEW_REQUIRED,
  // Zero/missing pay calculation on a real (non-scaffold) row. Scaffolds stay
  // coordinator-blocked via their evidence codes, not via ZERO_PAY.
  C.ZERO_PAY,
  'TIMESHEET_ZERO_PAY',
  // Whether >8h prices as ordinary (recorded 10-hour agreement) or overtime —
  // a pay-configuration decision over known hours.
  C.FT_OVER_8H_WITHOUT_10H_AGREEMENT,
  // Break-treatment pricing of known recorded time (see note above)
  C.BREAK_TREATMENT_BLOCKED,
  C.BREAK_TREATMENT_NOT_RESOLVED,
  C.BREAK_TREATMENT_FACTS_MISSING,
  C.BREAK_STATUS_UNKNOWN,
  C.BREAK_NESTED,
  'BREAK_TREATMENT_POSITIVE_FACT_REQUIRED',
  // Manual pay facts (allowance/sleepover evidence pricing known events)
  C.PAY_EVENT_FACTS_NOT_READY,
  C.PAY_FACT_NESTED,
  'PAY_FACT_SET_REQUIRED',
  'PAY_FACT_SET_NOT_APPROVED',
  'PAY_FACTS_MISSING',
  // Period aggregator pay-resolution codes
  'ASSIGNMENT_PAY_CONTEXT_BLOCKED',
  'PAY_IDENTITY_CORRECTION_PENDING',
  'PAY_IDENTITY_CORRECTION_READ_UNAVAILABLE',
  'PAY_IDENTITY_CORRECTION_PENDING_APPROVAL',
  'PAY_IDENTITY_CORRECTION_PENDING_RUN',
  'PAY_IDENTITY_CORRECTION_PENDING_SETTLEMENT',
  'PAY_IDENTITY_OVERPAYMENT_DECISION_PENDING',
  'PAYMENT_ATTESTED_BY_TWO_PEOPLE',
  'LEGACY_RECEIPT_TRANSITIONAL',
  'APPROVED_PENDING_RUN',
  'OVERPAYMENT_REVIEW',
  'ASSIGNMENT_SETUP_CHANGED_AFTER_APPROVAL',
  'ASSIGNMENT_PAY_CONTEXT_NOT_RESOLVED',
  'TIMESHEET_PAY_CONTEXT_NOT_RESOLVED',
  'TIMESHEET_PAY_CONTEXT_STALE',
  'RATE_SCHEDULE_GAP',
  'WORKER_MISSING_PAY_PROFILE',
  'EBA_EXPORT_NOT_READY',
  // Allowance / export-line reconciliation
  'PROVIDER_MANUAL_ALLOWANCE_RECONCILIATION_REQUIRED',
  'MANUAL_PAY_FACT_PROJECTION_PENDING',
  'ALLOWANCE_RECOMPUTE_REQUIRED',
  'ALLOWANCE_PAY_CERTIFICATION_REQUIRED',
  C.PROVIDER_MANUAL_ALLOWANCE_PRICING_PENDING,
  'XERO_COMPONENT_LINES_MISSING',
  // Approved-leave pay treatment (period readiness; payroll settles treatment)
  'LEAVE_ROSTER_EVIDENCE_GAP',
  'LEAVE_PAY_CONTEXT_BOUNDARY',
  'LEAVE_PAY_CONTEXT_MISSING',
  'APPROVED_LEAVE_PUBLIC_HOLIDAY_PAYMENT_EVIDENCE_MISSING',
  'PUBLIC_HOLIDAY_ABSENCE_AWARD_SUPPORT_REQUIRED',
  'PUBLIC_HOLIDAY_ABSENCE_STALE_PAYMENT',
  'PUBLIC_HOLIDAY_XERO_MAPPING_REQUIRED',
  'APPROVED_LEAVE_PAY_TREATMENT_UNSUPPORTED',
  // Leave worker-scope confirmation is a payroll decision (review or revoke)
  'LEAVE_WORKER_SCOPE_ADJUDICATED',
  // Pay-fact second approval on an already-approved timesheet
  'TIMESHEET_FACT_SET_APPROVAL_PENDING',
]);

/** Missing actual duration is a coordinator's time correction, not pay setup. */
const COORDINATOR_TIME_RESOLUTION_CODES = new Set<string>([C.BREAK_DURATION_UNRESOLVED]);

/**
 * Issue parameter value that moves one issue to payroll ownership. Only the
 * readiness summariser sets it, and only on BREAK_DURATION_UNRESOLVED raised
 * under V13 break semantics (payroll rebuild ON): the unknown unpaid break is a
 * payroll-owned pay HOLD (money held, care and approval never refused). Rebuild
 * OFF (LEGACY_28D1) never sets it, so the code keeps its 28d1 coordinator gate.
 */
export const ISSUE_PAY_HOLD_OWNER_PARAMETER = 'resolutionOwner';
const ISSUE_OWNER_OVERRIDE_CODES = new Set<string>([C.BREAK_DURATION_UNRESOLVED]);

/**
 * Resolution owner for one concrete issue. Identical to `blockerResolutionOwner`
 * except for the allow-listed V13 pay-hold override above; any other code or
 * parameter value falls back to the code's owner (fail closed).
 */
export function issueResolutionOwner(issue: {
  code: string;
  parameters?: Record<string, unknown> | null;
}): BlockerResolutionOwner {
  if (
    ISSUE_OWNER_OVERRIDE_CODES.has(issue.code) &&
    issue.parameters?.[ISSUE_PAY_HOLD_OWNER_PARAMETER] === 'payroll_pay'
  ) {
    return 'payroll_pay';
  }
  return blockerResolutionOwner(issue.code);
}

/**
 * Resolution owner for a readiness code. Total: unknown codes FAIL CLOSED to
 * coordinator_time (they keep blocking coordinator approval).
 *
 * Open-ended families classified by pattern (mirrors the impact carve-outs):
 * identity drift, export family (XERO_MAPPING_* / DRY_RUN_*), provider
 * responsibility evidence, APPROVED_LEAVE_* — all payroll-owned pay/export
 * resolution.
 */
export function blockerResolutionOwner(code: string): BlockerResolutionOwner {
  if (COORDINATOR_TIME_RESOLUTION_CODES.has(code)) return 'coordinator_time';
  if (PAYROLL_PAY_RESOLUTION_CODES.has(code)) return 'payroll_pay';
  if (isIdentityDriftCodePattern(code)) return 'payroll_pay';
  if (isExportFamilyCodePattern(code)) return 'payroll_pay';
  if (isProviderResponsibilityEvidenceCode(code)) return 'payroll_pay';
  if (isApprovedLeaveReadinessCode(code)) return 'payroll_pay';
  return 'coordinator_time';
}

/**
 * Does the tenant's enforcement posture keep payroll-owned pay codes blocking
 * COORDINATOR approval?
 *
 * Never. Enforcement mode governs the later money boundary; it must not turn
 * pay setup into a worked-time capture or approval outage. The pay-run snapshot
 * and export gates still refuse unresolved pay codes in every mode.
 */
export function payResolutionBlocksCoordinatorApproval(mode: PayContextEnforcementMode): boolean {
  void mode;
  return false;
}

/**
 * Issues that block COORDINATOR approval under `mode`.
 *
 * Every tenant mode: worked-time-integrity issues only. Payroll-owned pay codes
 * are carried on the approval result / audit payload and worked in the payroll
 * review queue instead.
 */
export function rowCoordinatorApprovalBlockingIssues(
  row: { issues?: readonly PayrollReadinessIssue[] | null } | null | undefined,
  mode: PayContextEnforcementMode,
): PayrollReadinessIssue[] {
  const blocking = rowApprovalBlockingIssues(row, mode);
  if (payResolutionBlocksCoordinatorApproval(mode)) return blocking;
  return blocking.filter((issue) => issueResolutionOwner(issue) === 'coordinator_time');
}

/** True iff any issue on the row blocks coordinator approval under `mode`. */
export function rowBlocksCoordinatorApproval(
  row: { issues?: readonly PayrollReadinessIssue[] | null } | null | undefined,
  mode: PayContextEnforcementMode,
): boolean {
  return rowCoordinatorApprovalBlockingIssues(row, mode).length > 0;
}

/**
 * Payroll-owned issues still outstanding on a row — the "approved, pay to be
 * finalised by payroll" set. Mode-independent: computed against the strict
 * gate (ENFORCE_CONTEXT) so the list is exactly what the pay-run snapshot /
 * export will still refuse on.
 */
export function rowOutstandingPayResolutionIssues(
  row: { issues?: readonly PayrollReadinessIssue[] | null } | null | undefined,
): PayrollReadinessIssue[] {
  return (row?.issues ?? []).filter(
    (issue) =>
      issueResolutionOwner(issue) === 'payroll_pay' &&
      blockerImpact(issue, 'ENFORCE_CONTEXT').blocksApproval,
  );
}

/** Deduplicated, sorted outstanding pay-resolution codes (audit/result payloads). */
export function outstandingPayResolutionCodes(
  row: { issues?: readonly PayrollReadinessIssue[] | null } | null | undefined,
): string[] {
  return Array.from(
    new Set(rowOutstandingPayResolutionIssues(row).map((issue) => issue.code)),
  ).sort();
}

/**
 * Safe default when tenant `pay_context_enforcement_mode` cannot be resolved.
 * Prefer strict enforcement over silently treating as OFF.
 */
export const DEFAULT_PAY_CONTEXT_ENFORCEMENT_MODE: PayContextEnforcementMode = 'ENFORCE_CONTEXT';

/**
 * Additive rollout default. A missing or unknown database value must preserve
 * the pre-existing roster behaviour until a tenant is explicitly enabled.
 */
export const DEFAULT_ROSTER_PAY_CONTEXT_ASSIGNMENT_POLICY: RosterPayContextAssignmentPolicy =
  'LEGACY_MODE_COUPLED';

/**
 * Parse a tenant_configs.pay_context_enforcement_mode value.
 * Unrecognised / missing values → DEFAULT_PAY_CONTEXT_ENFORCEMENT_MODE (safe for approval gate).
 */
export function parsePayContextEnforcementMode(
  value: string | null | undefined,
): PayContextEnforcementMode {
  if (
    value === 'OFF' ||
    value === 'SHADOW' ||
    value === 'ENFORCE_CONTEXT' ||
    value === 'ENFORCE_EXPORT'
  ) {
    return value;
  }
  return DEFAULT_PAY_CONTEXT_ENFORCEMENT_MODE;
}

/**
 * Parse tenant_configs.roster_pay_context_assignment_policy without weakening
 * existing tenants. Null and unrecognised values dual-read as legacy-coupled.
 */
export function parseRosterPayContextAssignmentPolicy(
  value: string | null | undefined,
): RosterPayContextAssignmentPolicy {
  return value === 'IDENTITY_ONLY' || value === 'LEGACY_MODE_COUPLED'
    ? value
    : DEFAULT_ROSTER_PAY_CONTEXT_ASSIGNMENT_POLICY;
}

/**
 * IDENTITY_ONLY is eligible only when the unchanged money-boundary mode is
 * enforcing. OFF/SHADOW must never move the hard boundary out of rostering.
 */
export function rosterPayContextIdentityOnlyIsActive(
  policy: RosterPayContextAssignmentPolicy,
  mode: PayContextEnforcementMode | 'UNKNOWN' | null | undefined,
): boolean {
  return policy === 'IDENTITY_ONLY' && (mode === 'ENFORCE_CONTEXT' || mode === 'ENFORCE_EXPORT');
}

/**
 * Issues on a readiness row that currently block payroll approval under `mode`.
 * Shared by UI Approve-disable and server `approveRosterTimesheet`.
 */
export function rowApprovalBlockingIssues(
  row: { issues?: readonly PayrollReadinessIssue[] | null } | null | undefined,
  mode: PayContextEnforcementMode,
): PayrollReadinessIssue[] {
  const issues = row?.issues ?? [];
  return issues.filter((issue) => blockerImpact(issue, mode).blocksApproval);
}

/** True iff any issue on the row has blocksApproval under `mode`. */
export function rowBlocksApproval(
  row: { issues?: readonly PayrollReadinessIssue[] | null } | null | undefined,
  mode: PayContextEnforcementMode,
): boolean {
  return rowApprovalBlockingIssues(row, mode).length > 0;
}
